import { forwardRef } from 'react'
import {
  Bell,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Icon — единый набор контурных (stroke) иконок на сетке 24 (`.ico` макета), включая «колокольчик».
 * Цвет наследуется от текста (currentColor), толщина линии единая. Значимые иконки получают
 * доступное имя через `title`/`aria-label`; декоративные — `aria-hidden`.
 */
const sizeMap = {
  sm: 'size-4', // 16
  md: 'size-5', // 20 (как .ico макета)
  lg: 'size-6', // 24
} as const

export interface IconProps extends Omit<LucideProps, 'ref' | 'size'> {
  /** Иконка из набора lucide (stroke, сетка 24). */
  as: LucideIcon
  size?: keyof typeof sizeMap
  /** Доступное имя; при отсутствии иконка считается декоративной (aria-hidden). */
  title?: string
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ as: Glyph, size = 'md', title, className, ...props }, ref) => {
    const decorative = !title
    return (
      <Glyph
        ref={ref}
        strokeWidth={1.7}
        aria-hidden={decorative || undefined}
        aria-label={title}
        role={decorative ? undefined : 'img'}
        className={cn('shrink-0 stroke-current', sizeMap[size], className)}
        {...props}
      />
    )
  },
)
Icon.displayName = 'Icon'

/** Готовая иконка-«колокольчик» уведомлений (требование Icon: добавить колокольчик). */
export { Bell as BellGlyph, ChevronDown as ChevronDownGlyph, ChevronRight as ChevronRightGlyph }
